/// <reference path="callCenter.module.ts" />
/// <reference path="../../../../typings/index.d.ts" />

module Meevo.Plugins.CallCenter {

    export class CallCenterSyncModalController {

        static $inject = [
            "meevo.components.common.IdentityService",
            "meevo.components.common.SecurityService",
            "meevo.components.common.DataService",
            "$state",
            "$rootScope",
             "$window", 
             "$mdDialog",
             "$translate",
             "meevo.AppSettings",
             "isRelockAvailable"
            ];

        private okButtonContentKey: string;
        private cancelButtonContentKey: string;
        private modalClass: string;
        private headerContentKey: string;
        private showOkButton: boolean;
        private showCancelButton: boolean;
        private message: string;
        private modalType: Components.Modal.ModalTypeEnum;
        private currentIdentity: Components.Common.IEmulationData;

        public constructor (private identityService: Meevo.Components.Common.IIdentityService,
            private securityService: Meevo.Components.Common.ISecurityService,
            private dataService: Meevo.Components.Common.ICommonDataService,
            private $state: ng.ui.IStateService,
            private $rootScope: angular.IRootScopeService,
            private $window: angular.IWindowService,
            private $mdDialog: angular.material.IDialogService,
            private $translate: angular.translate.ITranslateService,
            private appSettings: Meevo.Components.Common.IAppSettings,
            private isRelockAvailable: boolean) {

            this.configureModal();

            this.currentIdentity = this.identityService.emulationData;

            const eventHandler = (event) => {
                if (event.storageArea !== localStorage
                    || event.key !== Components.Common.Constants.CALL_CENTER_EMULATION_STORAGE) {
                    return;
                }

                const eventData = JSON.parse(event.newValue);
                const identityData = this.identityService.emulationData;

                /**
                 * Scenarios: 
                 *      1. Call Center was closed in another browser tab / window
                 *      2. Lock Screen was opened in another browser tab / window
                 */
                if (!eventData && !this.identityService.isEmulating) {
                    
                    this.dataService.getIdentity().then((newIdentity: Meevo.Components.Common.IIdentity) => {
                        
                        if (newIdentity.userId !== identityData.userId
                            || newIdentity.locationId !== identityData.loggedInLocation) {
                            return;
                        }

                        this.$mdDialog.hide(true);
                    });
                }

                /**
                 *  Scenario: Call Center emulation was changed in another browser window / tab. We
                 *  we can close the sync modal if all emulation data matches.
                 */
                if (eventData
                        && eventData.asImpersonateLocationId === identityData.asImpersonateLocationId
                        && eventData.effectiveCallCenterId === identityData.effectiveCallCenterId
                        && eventData.loggedInLocation === identityData.loggedInLocation
                        && eventData.userId === identityData.userId) {
                    
                    this.$mdDialog.hide(true);
                }
            };

            this.$window.removeEventListener('storage', eventHandler);
            this.$window.addEventListener('storage', eventHandler);
        }

        private configureModal = () => {

            if (!this.message) {
                this.message = this.isRelockAvailable
                    ? 'MESSAGE_CALL_CENTER_LOCK_SCREEN_DIFFERENT_USER'
                    : 'MESSAGE_CALL_CENTER_OUT_OF_SYNC';
            }

            this.message = this.$translate.instant(this.message);
            this.okButtonContentKey = this.isRelockAvailable 
                ? this.$translate.instant('LABEL_CONTINUE')
                : this.$translate.instant('LABEL_SYNC');
            this.cancelButtonContentKey = this.$translate.instant('LABEL_CANCEL');
            this.modalClass = 'modal-alert';
            this.headerContentKey = this.$translate.instant('HEADER_ALERT');
            this.showOkButton = true;
            this.showCancelButton = this.isRelockAvailable;
            this.modalType = Components.Modal.ModalTypeEnum.Alert;
        }

        cancel = () => {
            this.$rootScope.$broadcast(Meevo.Components.Common.Messages.SESSION_TIMEOUT_HANDLER, Meevo.Model.SessionState.Lockout, 'CallCenterSyncModal');
            this.$mdDialog.hide();
        }

        ok = () => {

            const localStorageEmulationData = JSON.parse(this.$window.localStorage.getItem(Components.Common.Constants.CALL_CENTER_EMULATION_STORAGE));
            const wasCallCenterClosed = Model.Guard.isNothing(localStorageEmulationData) || !localStorageEmulationData.effectiveCallCenterId;

            this.dataService.getIdentity().then((newIdentity: Meevo.Components.Common.IIdentity) => {

                /**
                 * Scenarios (Call Center is already opened in all scenarios):
                 *      1. User has changed the logged-in-location in another browser window / tab
                 *      2. A different user logged in from lock screen in another browser window / tab
                 */
                if (newIdentity.locationId !== this.currentIdentity.loggedInLocation
                    || newIdentity.userId !== this.currentIdentity.userId) {

                    this.$window.location.reload();
                    return;
                }

                this.identityService.setIdentity(newIdentity);
                this.$mdDialog.hide(true);
                
                /**
                 * Scenario: Call Center is open in this browser window / tab but was closed in another. Need to 
                 * refresh the front end with logged-in-location's security and then close the Call Center.
                 */
                if (wasCallCenterClosed) {
                    const callCenterContext = this.$state.get(this.appSettings.ROUTE_NAMES.callCenter);
                    this.securityService.refreshSecurityKeys();
                    this.$rootScope.$broadcast(Components.Common.Messages.CLOSE_CONTEXT, callCenterContext);
                    return;
                }

                /**
                 * Scenario: Call Center is open in this browser window / tab and emulation data was changed in another. 
                 * Broadcast the new identity's emulation data. The Call Center receives the message and will establish 
                 * the new emulation.
                 */
                if (!wasCallCenterClosed && Model.Guard.isSomething(this.currentIdentity.effectiveCallCenterId)) {
                    this.$rootScope.$broadcast(Components.Common.Messages.CALL_CENTER_EMULATION_CHANGE, { emulationData: {
                            asImpersonateLocationId: newIdentity.asImpersonateLocationId,
                            effectiveCallCenterId: newIdentity.effectiveCallCenterId,
                            loggedInLocation: newIdentity.locationId,
                            userId: newIdentity.userId
                        } });
                    return;
                }

                /**
                 * Scenario: Call Center is NOT opened yet in this browser window / tab but Call Center was opened and
                 * emulation initiated in another browser window / tab. Send user to Call Center state which will use the 
                 * identity data we refreshed (above) in its constructor to establish the emulation.
                 */
                this.$rootScope.$broadcast(Meevo.Components.Common.Messages.GOTO_STATE, {
                    stateName: this.appSettings.ROUTE_NAMES.callCenter
                });
            })
        }
    }

    angular.module("meevo.plugins.callCenter")
        .controller("meevo.plugins.callCenter.CallCenterSyncModalController", CallCenterSyncModalController);
}